import * as React from "react";
import { Alert, Card, Divider } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../components/antd/card/CardCollapsibleContent";
import { useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../gvc/testNewForm/CpDescription";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpArrayObject } from "../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemLoadBalancerPort } from "./CpArrayItemLoadBalancerPort";
import { LoadBalancerPortInput } from "../../../schema/types/workload/input";
import { v4 as uuidv4 } from "uuid";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpErrorWarningArrayIcon } from "../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../gvc/testNewForm/temp/CpKindSelect";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";

interface Props {}
export const WorkloadLoadBalancer: React.FC<Props> = ({}) => {
  const type = useCpFormValue(`type`);

  const isDirectEnabled = useCpFormValue(`loadBalancer.direct.isEnabled`);
  const isGeoLocationEnabled = useCpFormValue(`loadBalancer.geoLocation.isEnabled`);

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>
          Load Balancer
          <CpErrorWarningArrayIcon names={[`firewallConfig`]} />
        </CardTitle>
        <CardContent>
          <Divider orientation={`left`} orientationMargin={0}>
            Direct Load Balancer
          </Divider>
          <CpFormItem>
            <CpLabelTooltip>
              <CpLabel name={`loadBalancer.direct.isEnabled`}>Enabled</CpLabel>
              <CpSwitch name={`loadBalancer.direct.isEnabled`} />
              <CpTooltip
                tooltipProps={{
                  title: `Direct load balancers are created in each location that a workload is running in and are configured for
              the standard endpoints of the workload. Customers are responsible for configuring the workload with
              certificates if TLS is required`,
                }}
              />
            </CpLabelTooltip>
            <Alert
              className="mt-2 w-fit"
              type="warning"
              showIcon
              message="This feature is rarely needed. When enabled, TLS certificates will not be provided."
            />
          </CpFormItem>
          <CardCollapsibleContent collapsed={!isDirectEnabled}>
            <CpFormItem>
              <CpLabel name={`loadBalancer.direct.ipSet`}>IP Set</CpLabel>
              <CpKindSelect
                kind="ipset"
                name={`loadBalancer.direct.ipSet`}
                optionValue="name"
                style={{ maxWidth: 600 }}
              />
              <CpErrorWarning name={`loadBalancer.direct.ipSet`} />
            </CpFormItem>
            <CpArrayObject
              factoryFn={() => {
                const newItem: LoadBalancerPortInput = {
                  id: uuidv4(),
                  containerPort: "",
                  protocol: "TCP",
                  externalPort: "",
                  scheme: "",
                };
                return newItem;
              }}
              name={`loadBalancer.direct.ports`}
              label={`Ports`}
              labels={["External Port", "Protocol", "Container Port", "Scheme"]}
              description={`List of ports that will be exposed by this load balancer`}
            >
              <CpArrayItemLoadBalancerPort />
            </CpArrayObject>
          </CardCollapsibleContent>
          <Divider orientation={`left`} orientationMargin={0}>
            Geo Location Headers
          </Divider>
          <CpFormItem>
            <div className="flex items-center gap-2">
              <CpLabel name={`loadBalancer.geoLocation.isEnabled`}>Enabled</CpLabel>
              <CpSwitch name={`loadBalancer.geoLocation.isEnabled`} />
            </div>
            <CpDescription>
              When enabled, geo location headers will be included on inbound http requests. Existing headers will be
              replaced.
            </CpDescription>
          </CpFormItem>
          <CardCollapsibleContent collapsed={!isGeoLocationEnabled}>
            <div>
              <CpFormItem>
                <CpLabel name={`loadBalancer.geoLocation.headers.asn`}>ASN</CpLabel>
                <CpInput name={`loadBalancer.geoLocation.headers.asn`} style={{ width: 450 }} />
                <CpErrorWarning name={`loadBalancer.geoLocation.headers.asn`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`loadBalancer.geoLocation.headers.city`}>City</CpLabel>
                <CpInput name={`loadBalancer.geoLocation.headers.city`} style={{ width: 450 }} />
                <CpErrorWarning name={`loadBalancer.geoLocation.headers.city`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`loadBalancer.geoLocation.headers.country`}>Country</CpLabel>
                <CpInput name={`loadBalancer.geoLocation.headers.country`} style={{ width: 450 }} />
                <CpErrorWarning name={`loadBalancer.geoLocation.headers.country`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`loadBalancer.geoLocation.headers.region`}>Region</CpLabel>
                <CpInput name={`loadBalancer.geoLocation.headers.region`} style={{ width: 450 }} />
                <CpErrorWarning name={`loadBalancer.geoLocation.headers.region`} />
              </CpFormItem>
            </div>
          </CardCollapsibleContent>
          {type === "stateful" ? (
            <>
              <Divider orientation={`left`} orientationMargin={0}>
                Replica Direct
              </Divider>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`loadBalancer.replicaDirect`}>Enabled</CpLabel>
                  <CpSwitch name={`loadBalancer.replicaDirect`} />
                </div>
                <CpDescription>
                  <div>
                    <div>Allows to generate replica specific endpoints for this stateful workload. </div>
                    <div>Generated endpoints will have the format;</div>
                    <div className="font-mono">{`<workloadName>-<gvcAlias>-<replicaIndex>.<locationName>.controlplane.us`}</div>
                  </div>
                </CpDescription>
              </CpFormItem>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
